import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { toast } from 'sonner';
import { analyticsApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { 
  ChartLine, 
  Ticket, 
  WarningCircle, 
  Hourglass, 
  Star, 
  CheckCircle,
  Users,
  Funnel,
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  ListChecks,
  X
} from '@phosphor-icons/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

export const AdminAnalytics = () => {
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  // State
  const [overview, setOverview] = useState(null);
  const [charts, setCharts] = useState(null);
  const [staffReports, setStaffReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'staff'

  // Filtering & Sorting State for Staff
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [slaFilter, setSlaFilter] = useState('all'); // 'all' | 'breached'
  const [ratingThreshold, setRatingThreshold] = useState('all'); // 'all' | 'good' | 'bad'
  const [dateFilter, setDateFilter] = useState('all'); // 'all' | 'today' | 'yesterday' | 'week' | 'month'
  const [sortField, setSortField] = useState('total_count');
  const [sortDirection, setSortDirection] = useState('desc');

  const getDateParams = (filter) => {
    if (!filter || filter === 'all') return { start_date: undefined, end_date: undefined };
    
    const today = new Date();
    const formatDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    if (filter === 'today') {
      const dateStr = formatDate(today);
      return { start_date: dateStr, end_date: dateStr };
    }

    if (filter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      const dateStr = formatDate(yesterday);
      return { start_date: dateStr, end_date: dateStr };
    }

    if (filter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 7);
      return { start_date: formatDate(weekAgo), end_date: formatDate(today) };
    }

    if (filter === 'month') {
      const monthAgo = new Date();
      monthAgo.setDate(today.getDate() - 30);
      return { start_date: formatDate(monthAgo), end_date: formatDate(today) };
    }

    return { start_date: undefined, end_date: undefined };
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [overviewRes, chartsRes] = await Promise.all([
        analyticsApi.getOverview(),
        analyticsApi.getCharts()
      ]);
      setOverview(overviewRes.data);
      setCharts(chartsRes.data);
    } catch (error) {
      console.error('Failed to fetch overview/charts:', error);
      toast.error('Failed to load telemetry overview');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffReports = async (filterVal) => {
    setReportsLoading(true);
    try {
      const params = getDateParams(filterVal);
      const res = await analyticsApi.getStaffReports(params);
      setStaffReports(res.data || []);
    } catch (error) {
      console.error('Failed to fetch staff reports:', error);
      toast.error('Failed to load staff performance reports');
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchStaffReports(dateFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  // Selected Staff Details
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [staffDetail, setStaffDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerTab, setDrawerTab] = useState('tasks'); // 'tasks' | 'reviews'
  const [prevStaffId, setPrevStaffId] = useState(null);

  // Drawer local filters/sort states
  const [drawerTaskStatus, setDrawerTaskStatus] = useState('all'); // 'all' | 'completed' | 'active' | 'sla_breached'
  const [drawerTaskSort, setDrawerTaskSort] = useState('newest'); // 'newest' | 'oldest'
  const [drawerReviewFilter, setDrawerReviewFilter] = useState('all'); // 'all' | 'good' | 'bad'
  const [drawerReviewSort, setDrawerReviewSort] = useState('newest'); // 'newest' | 'oldest' | 'highest' | 'lowest'

  const fetchStaffDetail = async (id, filterVal) => {
    setDetailLoading(true);
    try {
      const params = getDateParams(filterVal);
      const { data } = await analyticsApi.getStaffReportDetail(id, params);
      setStaffDetail(data);
    } catch (error) {
      console.error('Failed to fetch staff detail report:', error);
      toast.error('Failed to load performance detail report');
      setSelectedStaffId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStaffId) {
      fetchStaffDetail(selectedStaffId, dateFilter);
      if (selectedStaffId !== prevStaffId) {
        setDrawerTab('tasks');
        setPrevStaffId(selectedStaffId);
        // Reset local drawer filters
        setDrawerTaskStatus('all');
        setDrawerTaskSort('newest');
        setDrawerReviewFilter('all');
        setDrawerReviewSort('newest');
      }
    } else {
      setStaffDetail(null);
      setPrevStaffId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId, dateFilter]);

  const filteredAndSortedStaff = useMemo(() => {
    let result = staffReports.filter(staff => {
      const matchesSearch = 
        staff.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        staff.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        staff.phone?.includes(searchQuery);
        
      const matchesRole = 
        roleFilter === 'all' || 
        (roleFilter === 'agent' && (staff.role === 'agent' || staff.role === 'foc')) ||
        staff.role === roleFilter;

      const matchesSla =
        slaFilter === 'all' ||
        (slaFilter === 'breached' && staff.sla_breaches > 0);

      const matchesRating =
        ratingThreshold === 'all' ||
        (ratingThreshold === 'good' && staff.rating_count > 0 && staff.rating_avg >= 4.0) ||
        (ratingThreshold === 'bad' && staff.rating_count > 0 && staff.rating_avg <= 3.0);

      return matchesSearch && matchesRole && matchesSla && matchesRating;
    });

    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [staffReports, searchQuery, roleFilter, slaFilter, ratingThreshold, sortField, sortDirection]);

  const staffKpis = useMemo(() => {
    if (!filteredAndSortedStaff.length) return { topOutput: null, topRated: null, fastest: null };

    let topOutput = null;
    let topRated = null;
    let fastest = null;

    filteredAndSortedStaff.forEach(staff => {
      // 1. Top Output
      if (!topOutput || staff.completed_count > topOutput.completed_count) {
        topOutput = staff;
      }
      
      // 2. Top Rated
      if (staff.rating_count > 0) {
        if (!topRated || staff.rating_avg > topRated.rating_avg) {
          topRated = staff;
        } else if (staff.rating_avg === topRated.rating_avg && staff.rating_count > topRated.rating_count) {
          topRated = staff; // tie-breaker: more reviews
        }
      }

      // 3. Fastest Resolver
      if (staff.completed_count > 0 && staff.mean_resolve_hours > 0) {
        if (!fastest || staff.mean_resolve_hours < fastest.mean_resolve_hours) {
          fastest = staff;
        }
      }
    });

    return { topOutput, topRated, fastest };
  }, [filteredAndSortedStaff]);

  const filteredAndSortedDrawerTasks = useMemo(() => {
    if (!staffDetail?.recent_tasks) return [];
    let result = [...staffDetail.recent_tasks];

    // Filter by status/SLA
    if (drawerTaskStatus !== 'all') {
      result = result.filter(task => {
        if (drawerTaskStatus === 'completed') {
          return task.status === 'completed' || task.status === 'closed';
        }
        if (drawerTaskStatus === 'active') {
          return task.status !== 'completed' && task.status !== 'closed' && task.status !== 'rejected';
        }
        if (drawerTaskStatus === 'sla_breached') {
          return task.is_breached === true;
        }
        return true;
      });
    }

    // Sort
    result.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return drawerTaskSort === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [staffDetail, drawerTaskStatus, drawerTaskSort]);

  const filteredAndSortedDrawerReviews = useMemo(() => {
    if (!staffDetail?.reviews) return [];
    let result = [...staffDetail.reviews];

    // Filter by stars
    if (drawerReviewFilter !== 'all') {
      result = result.filter(rev => {
        if (drawerReviewFilter === 'good') return rev.stars >= 4;
        if (drawerReviewFilter === 'bad') return rev.stars <= 3;
        return true;
      });
    }

    // Sort
    result.sort((a, b) => {
      if (drawerReviewSort === 'newest') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (drawerReviewSort === 'oldest') {
        return new Date(a.created_at) - new Date(b.created_at);
      }
      if (drawerReviewSort === 'highest') {
        return b.stars - a.stars;
      }
      if (drawerReviewSort === 'lowest') {
        return a.stars - b.stars;
      }
      return 0;
    });

    return result;
  }, [staffDetail, drawerReviewFilter, drawerReviewSort]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;

  // Calculate breach rate percentage
  const total = overview?.total_tickets || 0;
  const breached = overview?.breached_tickets || 0;
  const complianceRate = total > 0 ? Math.round(((total - breached) / total) * 100) : 100;

  // Recharts colors
  const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'];

  return (
    <DashboardLayout role="super_admin">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
        {/* Header & Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-5">
          <div className="space-y-1">
            <h1 className="text-4xl font-black tracking-tight">Performance & Telemetry</h1>
            <p className="text-muted-foreground">Monitor service desk operations, SLA compliance, and technician workload telemetry</p>
          </div>
          
          <div className="flex bg-slate-100 p-1 rounded-lg self-start sm:self-center">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${
                activeTab === 'overview'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Overview & Charts
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${
                activeTab === 'staff'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="staff-reports-tab"
            >
              Staff Reports
            </button>
          </div>
        </div>

        {activeTab === 'overview' ? (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Card 1: SLA Compliance */}
              <Card className="p-5 border flex items-center justify-between">
                <div className="space-y-1">
                  <p className="label-micro text-slate-400">SLA Compliance Rate</p>
                  <p className={`text-3xl font-black ${complianceRate >= 90 ? 'text-emerald-600' : 'text-amber-500'}`}>
                    {complianceRate}%
                  </p>
                  <p className="text-xs text-slate-500 font-medium">Target: &gt; 95% compliance</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-sm">
                  <CheckCircle size={28} className={complianceRate >= 90 ? 'text-emerald-500' : 'text-amber-500'} weight="fill" />
                </div>
              </Card>

              {/* Card 2: Open Tickets */}
              <Card className="p-5 border flex items-center justify-between">
                <div className="space-y-1">
                  <p className="label-micro text-slate-400">Active Open Tickets</p>
                  <p className="text-3xl font-black text-slate-800">{overview?.open_tickets || 0}</p>
                  <p className="text-xs text-slate-500 font-medium">Pending dispatcher routing</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-sm text-primary" style={{ color: brandColorAccent }}>
                  <Ticket size={28} weight="fill" />
                </div>
              </Card>

              {/* Card 3: Avg Resolution */}
              <Card className="p-5 border flex items-center justify-between">
                <div className="space-y-1">
                  <p className="label-micro text-slate-400">Avg Resolution Time</p>
                  <p className="text-3xl font-black text-slate-800">{overview?.avg_resolution_hours || 0} hrs</p>
                  <p className="text-xs text-slate-500 font-medium">Ticket creation to closure</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-sm text-sky-500">
                  <Hourglass size={28} weight="fill" />
                </div>
              </Card>

              {/* Card 4: CSAT Rating */}
              <Card className="p-5 border flex items-center justify-between">
                <div className="space-y-1">
                  <p className="label-micro text-slate-400">Customer Satisfaction</p>
                  <p className="text-3xl font-black text-slate-800">{overview?.average_rating || 0.0} / 5.0</p>
                  <p className="text-xs text-slate-500 font-medium">Based on feedback ratings</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-sm text-yellow-500">
                  <Star size={28} weight="fill" />
                </div>
              </Card>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Chart 1: Ticket Volume Trends (AreaChart) */}
              <Card className="p-6 border lg:col-span-2 space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <ChartLine size={20} style={{ color: brandColorAccent }} />
                    Ticket volume Trends
                  </h3>
                  <p className="text-xs text-slate-400">Daily incoming vs. resolved ticket volume logs</p>
                </div>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={charts?.volume_by_day}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={brandColorAccent} stopOpacity={0.2}/>
                          <stop offset="95%" stopColor={brandColorAccent} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '4px' }} />
                      <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                      <Area type="monotone" name="New Tickets" dataKey="created" stroke={brandColorAccent} strokeWidth={2.5} fillOpacity={1} fill="url(#colorCreated)" />
                      <Area type="monotone" name="Resolved Jobs" dataKey="resolved" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorResolved)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Chart 2: SLA Status distribution (PieChart) */}
              <Card className="p-6 border space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <WarningCircle size={20} className="text-rose-500" />
                    SLA Compliance Breakdown
                  </h3>
                  <p className="text-xs text-slate-400 font-medium">Compliance against total resolution deadlines</p>
                </div>
                <div className="h-[230px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={charts?.sla_status}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {charts?.sla_status?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#10b981' : '#ef4444'} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 text-xs font-bold pt-2">
                  <div className="flex items-center gap-1.5 text-emerald-600">
                    <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                    Met ({charts?.sla_status?.[0]?.value || 0})
                  </div>
                  <div className="flex items-center gap-1.5 text-rose-600">
                    <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                    Breached ({charts?.sla_status?.[1]?.value || 0})
                  </div>
                </div>
              </Card>

              {/* Chart 3: Technician Utilization (BarChart) */}
              <Card className="p-6 border lg:col-span-2 space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Users size={20} className="text-indigo-500" />
                    Technician Workload & Proximity
                  </h3>
                  <p className="text-xs text-slate-400">Comparing active tickets load and performance metrics</p>
                </div>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={charts?.tech_workload}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ fontSize: '12px' }} />
                      <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                      <Bar name="Active Job Cards" dataKey="active_jobs" fill={brandColorAccent} radius={[4, 4, 0, 0]} maxBarSize={45} />
                      <Bar name="Performance Rating (Stars)" dataKey="rating" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={45} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Chart 4: Ticket Status Breakdown */}
              <Card className="p-6 border space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Status Distribution</h3>
                  <p className="text-xs text-slate-400">Total tickets categorised by lifecycle stage</p>
                </div>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pt-2">
                  {charts?.status_distribution?.map((item, idx) => {
                    const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
                    return (
                      <div key={item.name} className="space-y-1 text-xs">
                        <div className="flex justify-between font-bold text-slate-700">
                          <span>{item.name}</span>
                          <span>{item.value} ({percentage}%)</span>
                        </div>
                        <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full" 
                            style={{ 
                              width: `${percentage}%`,
                              backgroundColor: COLORS[idx % COLORS.length]
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {/* Staff KPI Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Card 1: Highest Output */}
              <Card className="p-5 border flex items-center justify-between bg-emerald-50/20 border-emerald-100 shadow-sm">
                <div className="space-y-1">
                  <p className="label-micro text-emerald-800 font-bold uppercase tracking-wider">Highest Output Staff</p>
                  <p className="text-3xl font-black text-emerald-700">
                    {staffKpis.topOutput ? `${staffKpis.topOutput.completed_count} completed` : '0 completed'}
                  </p>
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <span className="text-xs text-slate-600 font-medium">
                      {staffKpis.topOutput?.full_name || 'No data'}
                    </span>
                    {staffKpis.topOutput && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                        {staffKpis.topOutput.role}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-sm text-emerald-600">
                  <ListChecks size={28} weight="fill" />
                </div>
              </Card>

              {/* Card 2: Top Rated */}
              <Card className="p-5 border flex items-center justify-between bg-amber-50/20 border-amber-100 shadow-sm">
                <div className="space-y-1">
                  <p className="label-micro text-amber-800 font-bold uppercase tracking-wider">Top Rated Performance</p>
                  <p className="text-3xl font-black text-amber-700">
                    {staffKpis.topRated ? `${staffKpis.topRated.rating_avg.toFixed(1)} / 5.0` : 'N/A'}
                  </p>
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <span className="text-xs text-slate-600 font-medium">
                      {staffKpis.topRated ? `${staffKpis.topRated.full_name} (${staffKpis.topRated.rating_count} ratings)` : 'No data'}
                    </span>
                    {staffKpis.topRated && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        {staffKpis.topRated.role}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-amber-50 rounded-sm text-amber-500">
                  <Star size={28} weight="fill" />
                </div>
              </Card>

              {/* Card 3: Fastest Resolver */}
              <Card className="p-5 border flex items-center justify-between bg-blue-50/20 border-blue-100 shadow-sm">
                <div className="space-y-1">
                  <p className="label-micro text-blue-800 font-bold uppercase tracking-wider">Fastest Resolve Speed</p>
                  <p className="text-3xl font-black text-blue-700">
                    {staffKpis.fastest ? `${staffKpis.fastest.mean_resolve_hours.toFixed(1)} hrs` : 'N/A'}
                  </p>
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <span className="text-xs text-slate-600 font-medium">
                      {staffKpis.fastest?.full_name || 'No data'}
                    </span>
                    {staffKpis.fastest && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                        {staffKpis.fastest.role}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-sm text-blue-500">
                  <Hourglass size={28} weight="fill" />
                </div>
              </Card>
            </div>

            {/* Filters Bar */}
            <div className="space-y-3">
              {/* Row 1: Search & Date Range */}
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search staff by name, email, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-sm bg-white"
                    data-testid="staff-search-input"
                  />
                </div>
                
                {/* Date range selector */}
                <div className="relative flex items-center bg-white border rounded-lg px-3 py-2 text-sm text-slate-600 min-w-[170px]">
                  <span className="text-[10px] uppercase font-bold text-slate-400 mr-2">Timeframe:</span>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="bg-transparent focus:outline-none font-bold text-xs uppercase cursor-pointer flex-1"
                    data-testid="staff-date-select"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Advanced Filters & Sorting */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Role filter */}
                <div className="relative flex items-center bg-white border rounded-lg px-3 py-2 text-xs text-slate-600">
                  <Funnel size={14} className="mr-2 text-slate-400" />
                  <span className="font-bold text-[10px] uppercase text-slate-400 mr-2">Role:</span>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="bg-transparent focus:outline-none font-bold text-xs uppercase cursor-pointer"
                    data-testid="staff-role-select"
                  >
                    <option value="all">All Roles</option>
                    <option value="agent">Agents & FOC</option>
                    <option value="technician">Technicians</option>
                  </select>
                </div>

                {/* Rating filter */}
                <div className="relative flex items-center bg-white border rounded-lg px-3 py-2 text-xs text-slate-600">
                  <Star size={14} className="mr-2 text-slate-400" />
                  <span className="font-bold text-[10px] uppercase text-slate-400 mr-2">CSAT Rating:</span>
                  <select
                    value={ratingThreshold}
                    onChange={(e) => setRatingThreshold(e.target.value)}
                    className="bg-transparent focus:outline-none font-bold text-xs uppercase cursor-pointer"
                    data-testid="staff-rating-select"
                  >
                    <option value="all">All Ratings</option>
                    <option value="good">Good (≥ 4.0⭐)</option>
                    <option value="bad">Underperforming (≤ 3.0⭐)</option>
                  </select>
                </div>

                {/* SLA breaches filter */}
                <div className="relative flex items-center bg-white border rounded-lg px-3 py-2 text-xs text-slate-600">
                  <WarningCircle size={14} className="mr-2 text-slate-400" />
                  <span className="font-bold text-[10px] uppercase text-slate-400 mr-2">SLA Status:</span>
                  <select
                    value={slaFilter}
                    onChange={(e) => setSlaFilter(e.target.value)}
                    className="bg-transparent focus:outline-none font-bold text-xs uppercase cursor-pointer"
                    data-testid="staff-sla-select"
                  >
                    <option value="all">All Compliance</option>
                    <option value="breached">Has SLA Breaches</option>
                  </select>
                </div>

                {/* Explicit Sorting controls */}
                <div className="relative flex items-center bg-white border rounded-lg px-3 py-2 text-xs text-slate-600 ml-auto md:ml-0">
                  <span className="font-bold text-[10px] uppercase text-slate-400 mr-2">Sort:</span>
                  <select
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value)}
                    className="bg-transparent focus:outline-none font-bold text-xs uppercase cursor-pointer mr-2 border-r pr-2 border-slate-200"
                    data-testid="staff-sort-field-select"
                  >
                    <option value="full_name">Name</option>
                    <option value="total_count">Total Workload</option>
                    <option value="completed_count">Completed Work</option>
                    <option value="active_count">Active Workload</option>
                    <option value="sla_breaches">SLA Breaches</option>
                    <option value="rating_avg">Avg Rating</option>
                    <option value="mean_resolve_hours">Mean Speed</option>
                  </select>
                  <select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value)}
                    className="bg-transparent focus:outline-none font-bold text-xs uppercase cursor-pointer"
                    data-testid="staff-sort-dir-select"
                  >
                    <option value="desc">Highest First</option>
                    <option value="asc">Lowest First</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <Card className="border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider">
                      {[
                        { field: 'full_name', label: 'Name & Role' },
                        { field: 'total_count', label: 'Total Volume' },
                        { field: 'completed_count', label: 'Completed' },
                        { field: 'active_count', label: 'Active Workload' },
                        { field: 'sla_breaches', label: 'SLA Breaches' },
                        { field: 'rating_avg', label: 'Avg Rating' },
                        { field: 'mean_resolve_hours', label: 'Mean Speed' }
                      ].map(col => {
                        const isSorted = sortField === col.field;
                        return (
                          <th
                            key={col.field}
                            onClick={() => handleSort(col.field)}
                            className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                          >
                            <div className="flex items-center gap-1.5">
                              {col.label}
                              {isSorted ? (
                                sortDirection === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />
                              ) : null}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {reportsLoading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-12">
                          <div className="flex flex-col items-center justify-center gap-2.5">
                            <LoadingSpinner />
                            <span className="text-xs text-slate-400 font-medium">Loading telemetry reports...</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredAndSortedStaff.length > 0 ? (
                      filteredAndSortedStaff.map(staff => (
                        <tr 
                          key={staff.id} 
                          onClick={() => setSelectedStaffId(staff.id)}
                          className="hover:bg-slate-50/50 transition-colors cursor-pointer select-none" 
                          data-testid={`staff-row-${staff.full_name}`}
                        >
                          <td className="px-6 py-4">
                            <div className="font-semibold text-slate-800">{staff.full_name}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{staff.email || staff.phone}</div>
                            <span className={`inline-block text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full mt-1.5 ${
                              staff.role === 'technician' 
                                ? 'bg-green-50 text-green-700 border border-green-200' 
                                : staff.role === 'foc'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}>
                              {staff.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-semibold text-slate-700">
                            {staff.total_count} {staff.role === 'technician' ? 'jobs' : 'tickets'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1 text-emerald-600 font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              {staff.completed_count}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1 text-blue-600 font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                              {staff.active_count}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {staff.sla_breaches > 0 ? (
                              <span className="text-red-500 font-black flex items-center gap-1">
                                <WarningCircle size={14} weight="bold" />
                                {staff.sla_breaches}
                              </span>
                            ) : (
                              <span className="text-slate-400 font-semibold">0</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {staff.rating_count > 0 ? (
                              <div className="flex items-center gap-1">
                                <Star size={14} className="text-yellow-500" weight="fill" />
                                <span className="font-bold text-slate-700">{staff.rating_avg.toFixed(1)}</span>
                                <span className="text-xs text-slate-400">({staff.rating_count})</span>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">No reviews</span>
                            )}
                          </td>
                          <td className="px-6 py-4 font-semibold text-slate-700">
                            {staff.mean_resolve_hours > 0 ? (
                              <span>{staff.mean_resolve_hours.toFixed(1)} hrs</span>
                            ) : (
                              <span className="text-slate-400 font-normal">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-10 text-slate-400">
                          No staff members found matching criteria
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedStaffId && (
        <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setSelectedStaffId(null)}
          />
          
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            .animate-slide-in-right {
              animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
          `}</style>
          
          {/* Panel */}
          <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col z-10 animate-slide-in-right">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-full text-white flex items-center justify-center font-bold text-xl shadow-inner"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {staffDetail?.user?.full_name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-black text-slate-800">
                      {staffDetail?.user?.full_name || 'Loading...'}
                    </h2>
                    {staffDetail?.user?.role && (
                      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        staffDetail.user.role === 'technician'
                          ? 'bg-green-100 text-green-800'
                          : staffDetail.user.role === 'foc'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {staffDetail.user.role}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {staffDetail?.user?.email} • {staffDetail?.user?.phone}
                  </p>
                </div>
              </div>
              
              <button 
                onClick={() => setSelectedStaffId(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
                title="Close panel"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {detailLoading ? (
                <div className="h-full flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              ) : staffDetail ? (
                <>
                  {/* Date Filter in Drawer */}
                  <div className="flex justify-between items-center bg-slate-50 border border-slate-100 rounded-lg p-3 text-sm">
                    <span className="text-slate-500 font-bold flex items-center gap-1.5 text-xs uppercase tracking-wider">
                      Report Timeframe Filter
                    </span>
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="bg-white border rounded px-2.5 py-1 font-bold text-xs uppercase cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary text-slate-700"
                      data-testid="drawer-date-select"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="week">Last 7 Days</option>
                      <option value="month">Last 30 Days</option>
                    </select>
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-4 gap-3">
                    <Card className="p-3 border text-center shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Completed</p>
                      <p className="text-xl font-black text-emerald-600">{staffDetail.stats.completed_count}</p>
                    </Card>
                    <Card className="p-3 border text-center shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Active</p>
                      <p className="text-xl font-black text-blue-600">{staffDetail.stats.active_count}</p>
                    </Card>
                    <Card className="p-3 border text-center shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">SLA Breaches</p>
                      <p className={`text-xl font-black ${staffDetail.stats.sla_breaches > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                        {staffDetail.stats.sla_breaches}
                      </p>
                    </Card>
                    <Card className="p-3 border text-center shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Avg Rating</p>
                      <p className="text-xl font-black text-amber-500">
                        {staffDetail.stats.rating_count > 0 ? `${staffDetail.stats.rating_avg.toFixed(1)} ⭐` : 'N/A'}
                      </p>
                    </Card>
                  </div>
                  
                  {/* Mean Speed Info */}
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 flex items-center justify-between text-sm">
                    <span className="text-slate-500 font-medium flex items-center gap-1.5">
                      <Hourglass size={16} className="text-sky-500" />
                      Mean Resolve Time / Duration
                    </span>
                    <span className="font-bold text-slate-700">
                      {staffDetail.stats.mean_resolve_hours > 0 ? `${staffDetail.stats.mean_resolve_hours.toFixed(1)} hours` : 'No completed tasks yet'}
                    </span>
                  </div>

                  {/* Drawer Tabs */}
                  <div className="space-y-4">
                    <div className="flex border-b border-slate-100 gap-4">
                      <button
                        onClick={() => setDrawerTab('tasks')}
                        className={`pb-2.5 text-sm font-bold border-b-2 transition-all ${
                          drawerTab === 'tasks'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        Recent Tasks ({filteredAndSortedDrawerTasks.length})
                      </button>
                      <button
                        onClick={() => setDrawerTab('reviews')}
                        className={`pb-2.5 text-sm font-bold border-b-2 transition-all ${
                          drawerTab === 'reviews'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                        data-testid="drawer-reviews-tab"
                      >
                        Customer Feedback ({filteredAndSortedDrawerReviews.length})
                      </button>
                    </div>

                    {/* Tab contents */}
                    {drawerTab === 'tasks' ? (
                      <div className="space-y-3">
                        {/* Tasks Sub-filters */}
                        <div className="flex gap-2 items-center bg-slate-50/50 p-2 rounded-lg border border-slate-100 text-xs">
                          <span className="font-bold text-[10px] uppercase text-slate-400">Status:</span>
                          <select
                            value={drawerTaskStatus}
                            onChange={(e) => setDrawerTaskStatus(e.target.value)}
                            className="bg-white border rounded px-1.5 py-0.5 font-semibold text-xs cursor-pointer text-slate-600 focus:outline-none"
                            data-testid="drawer-task-status-select"
                          >
                            <option value="all">All Statuses</option>
                            <option value="completed">Completed / Closed</option>
                            <option value="active">Active Workload</option>
                            <option value="sla_breached">SLA Breached</option>
                          </select>

                          <span className="font-bold text-[10px] uppercase text-slate-400 ml-auto">Sort:</span>
                          <select
                            value={drawerTaskSort}
                            onChange={(e) => setDrawerTaskSort(e.target.value)}
                            className="bg-white border rounded px-1.5 py-0.5 font-semibold text-xs cursor-pointer text-slate-600 focus:outline-none"
                            data-testid="drawer-task-sort-select"
                          >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                          </select>
                        </div>

                        {filteredAndSortedDrawerTasks.length > 0 ? (
                          filteredAndSortedDrawerTasks.map(task => (
                            <Card key={task.id} className="p-4 border hover:bg-slate-50/50 transition-colors">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    {staffDetail.user.role === 'technician' ? (
                                      <span className="font-bold text-slate-800">{task.ticket_number}</span>
                                    ) : (
                                      <Link 
                                        to={`/ops/admin/tickets/${task.id}`} 
                                        className="font-bold text-primary hover:underline"
                                      >
                                        {task.ticket_number}
                                      </Link>
                                    )}
                                    {task.priority && (
                                      <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                        task.priority === 'urgent'
                                          ? 'bg-rose-50 text-rose-600'
                                          : task.priority === 'low'
                                          ? 'bg-slate-100 text-slate-600'
                                          : 'bg-blue-50 text-blue-600'
                                      }`}>
                                        {task.priority}
                                      </span>
                                    )}
                                    {task.is_breached && (
                                      <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-600 flex items-center gap-0.5 border border-red-200">
                                        <WarningCircle size={10} weight="bold" />
                                        SLA Breached
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">
                                    Customer: <span className="font-semibold text-slate-700">{task.customer_name}</span>
                                  </p>
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    Product: {task.product_name}
                                  </p>
                                </div>
                                
                                <div className="text-right">
                                  <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                                    task.status === 'completed' || task.status === 'closed'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : task.status === 'rejected'
                                      ? 'bg-rose-50 text-rose-700'
                                      : 'bg-amber-50 text-amber-700'
                                  }`}>
                                    {task.status}
                                  </span>
                                  <p className="text-[10px] text-slate-400 mt-2">
                                    Created: {new Date(task.created_at).toLocaleDateString()}
                                  </p>
                                  {task.completed_at && (
                                    <p className="text-[10px] text-emerald-600 mt-0.5">
                                      Done: {new Date(task.completed_at).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </Card>
                          ))
                        ) : (
                          <div className="text-center py-10 text-slate-400 text-sm">
                            No recent tasks found for this staff member
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Reviews Sub-filters */}
                        <div className="flex gap-2 items-center bg-slate-50/50 p-2 rounded-lg border border-slate-100 text-xs">
                          <span className="font-bold text-[10px] uppercase text-slate-400">CSAT Score:</span>
                          <select
                            value={drawerReviewFilter}
                            onChange={(e) => setDrawerReviewFilter(e.target.value)}
                            className="bg-white border rounded px-1.5 py-0.5 font-semibold text-xs cursor-pointer text-slate-600 focus:outline-none"
                            data-testid="drawer-review-filter-select"
                          >
                            <option value="all">All Ratings</option>
                            <option value="good">Good (≥ 4⭐)</option>
                            <option value="bad">Underperforming (≤ 3⭐)</option>
                          </select>

                          <span className="font-bold text-[10px] uppercase text-slate-400 ml-auto">Sort:</span>
                          <select
                            value={drawerReviewSort}
                            onChange={(e) => setDrawerReviewSort(e.target.value)}
                            className="bg-white border rounded px-1.5 py-0.5 font-semibold text-xs cursor-pointer text-slate-600 focus:outline-none"
                            data-testid="drawer-review-sort-select"
                          >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="highest">Highest Rating</option>
                            <option value="lowest">Lowest Rating</option>
                          </select>
                        </div>

                        {filteredAndSortedDrawerReviews.length > 0 ? (
                          filteredAndSortedDrawerReviews.map((rev, idx) => (
                            <Card key={idx} className="p-4 border space-y-2.5">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-0.5">
                                  {[...Array(5)].map((_, i) => (
                                    <Star 
                                      key={i} 
                                      size={14} 
                                      className={i < rev.stars ? "text-yellow-500" : "text-slate-200"} 
                                      weight={i < rev.stars ? "fill" : "regular"} 
                                    />
                                  ))}
                                </div>
                                <span className="text-[10px] text-slate-400">
                                  {new Date(rev.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              
                              {rev.comment ? (
                                <p className="text-sm italic text-slate-600 bg-slate-50/50 p-2.5 rounded border border-slate-100/50">
                                  "{rev.comment}"
                                </p>
                              ) : (
                                <p className="text-xs italic text-slate-400">No review text left</p>
                              )}
                              
                              <div className="flex justify-between items-center text-xs text-slate-400 pt-0.5">
                                <span>Customer: <strong className="text-slate-600">{rev.customer_name}</strong></span>
                                <span>Ref: <strong className="text-slate-600">{rev.ticket_number}</strong></span>
                              </div>
                            </Card>
                          ))
                        ) : (
                          <div className="text-center py-10 text-slate-400 text-sm">
                            No matching customer feedback reviews found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-10 text-slate-400">
                  Failed to load report
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};
